import React, { useState } from "react";
import useApiEp from "../hooks/useApiEp";
import MuPb from "../widgets/MuPb";
import {
  Alert,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Link,
  Modal,
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Slide,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getErrorResponse } from "../../utils/util";
import BackBtn from "../widgets/BackBtn";
import { API_ENDPOINT } from "../../constants/constants";
import { getHeader } from "../../utils/util";
import { LoadingButton } from "@mui/lab";

function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

const ToolsPayments = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // Fixed limit, can make dynamic if needed
  const [paymentStatus, setPaymentStatus] = useState("");
  const [orderStatus, setOrderStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState({
    open: false,
    orderId: null,
  });
  const [newPaymentStatus, setNewPaymentStatus] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...(paymentStatus && { paymentStatus }),
    ...(orderStatus && { orderStatus }),
  });

  const url = `admin/tools-transactions?${queryParams.toString()}`;

  const { data, isLoading, isError, error, refetch } = useApiEp(url);

  const showNotification = (message, type = "success") => {
    setNotification({
      open: true,
      message,
      type,
    });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handlePaymentDialogOpen = (orderId, currentStatus) => {
    setNewPaymentStatus(currentStatus || "");
    setTransactionId("");
    setPaymentDate("");
    setPaymentDialog({ open: true, orderId });
  };

  const handlePaymentDialogClose = () => {
    setPaymentDialog({ open: false, orderId: null });
    setNewPaymentStatus("");
    setTransactionId("");
    setPaymentDate("");
  };

  const handlePaymentUpdate = async () => {
    if (!newPaymentStatus) {
      showNotification("Please select a payment status.", "error");
      return;
    }

    if (newPaymentStatus === "paid") {
      if (!transactionId.trim()) {
        showNotification("Please enter a transaction ID.", "error");
        return;
      }
      if (!paymentDate) {
        showNotification("Please enter a payment date.", "error");
        return;
      }
    }

    try {
      setIsUpdating(true);

      let requestBody = { status: newPaymentStatus };
      if (newPaymentStatus === "paid") {
        requestBody.transactionId = transactionId.trim();
        requestBody.paymentDate = paymentDate;
      }

      const response = await fetch(
        `${API_ENDPOINT}admin/update-order-payment-status/${paymentDialog.orderId}`,
        {
          method: "POST",
          ...getHeader(),
          body: JSON.stringify(requestBody),
        }
      );

      if (response.ok) {
        setPaymentDialog({ open: false, orderId: null });
        setNewPaymentStatus("");
        setTransactionId("");
        setPaymentDate("");
        showNotification(
          `Payment status updated to ${newPaymentStatus} successfully!`,
          "success"
        );
        await refetch();
      } else {
        showNotification(
          "Failed to update payment status. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Failed to update payment status:", error);
      showNotification("An error occurred. Please try again.", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handlePaymentStatusChange = (event) => {
    setPaymentStatus(event.target.value);
    setPage(1);
  };

  const handleOrderStatusChange = (event) => {
    setOrderStatus(event.target.value);
    setPage(1);
  };

  const handleOpen = (url) => {
    setSelectedReceipt(url);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedReceipt(null);
  };

  return (
    <Container>
      <BackBtn />
      <div style={{ display: "flex", marginBottom: "16px" }}>
        <FormControl sx={{ m: 1, minWidth: 180 }}>
          <InputLabel>Payment Status</InputLabel>
          <Select
            value={paymentStatus}
            label="Payment Status"
            onChange={handlePaymentStatusChange}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ m: 1, minWidth: 180 }}>
          <InputLabel>Order Status</InputLabel>
          <Select
            value={orderStatus}
            label="Order Status"
            onChange={handleOrderStatusChange}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="accepted">Accepted</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
      </div>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order ID</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell>Payment Status</TableCell>
              <TableCell>Order Status</TableCell>
              <TableCell>Receipt</TableCell>
              <TableCell>Payment Date</TableCell>
              <TableCell>Transaction ID</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.data?.map((item) => (
              <TableRow key={item._id}>
                <TableCell>
                  <a target="_blank" href={`/home/tools-order/${item.orderId}`}>
                    {item.orderId}
                  </a>
                </TableCell>
                <TableCell>{item.paymentMethod}</TableCell>
                <TableCell>{item.paymentStatus}</TableCell>
                <TableCell>{item.orderStatus}</TableCell>
                <TableCell>
                  {item.receiptUrl ? (
                    <>
                      <Button
                        variant="text"
                        onClick={() => handleOpen(item.receiptUrl)}
                        sx={{ mr: 1 }}
                      >
                        View
                      </Button>
                    </>
                  ) : (
                    "N/A"
                  )}
                </TableCell>
                <TableCell>
                  {item.paymentDate
                    ? new Date(item.paymentDate).toLocaleDateString()
                    : "N/A"}
                </TableCell>
                <TableCell>{item.transactionId || "N/A"}</TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() =>
                      handlePaymentDialogOpen(item.orderId, item.paymentStatus)
                    }
                  >
                    Update Status
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        count={data?.pagination?.totalPages || 1}
        page={page}
        onChange={handlePageChange}
        sx={{ mt: 2, display: "flex", justifyContent: "center" }}
      />
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="receipt-modal-title"
        aria-describedby="receipt-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            maxWidth: "90vw",
            maxHeight: "90vh",
            overflow: "auto",
            borderRadius: 2,
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
          {selectedReceipt && (
            <img
              src={selectedReceipt}
              alt="Receipt"
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                display: "block",
                margin: "0 auto",
              }}
            />
          )}
        </Box>
      </Modal>
      {/* Payment Status Update Dialog */}
      <Dialog
        open={paymentDialog.open}
        onClose={handlePaymentDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Payment Status</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Select the new payment status for this order:
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Payment Status</InputLabel>
            <Select
              value={newPaymentStatus}
              label="Payment Status"
              onChange={(e) => setNewPaymentStatus(e.target.value)}
              autoFocus
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
            </Select>
          </FormControl>
          {newPaymentStatus === "paid" && (
            <>
              <TextField
                fullWidth
                label="Transaction ID"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                required
                sx={{ mt: 2 }}
              />
              <TextField
                fullWidth
                label="Payment Date"
                type="date"
                value={paymentDate}
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={(e) => setPaymentDate(e.target.value)}
                required
                sx={{ mt: 2 }}
                inputProps={{
                  min: new Date().toISOString().split("T")[0],
                }}
              />
            </>
          )}
          {newPaymentStatus === "rejected" && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This action cannot be undone. The customer will be notified.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePaymentDialogClose}>Cancel</Button>
          <LoadingButton
            variant="contained"
            loading={isUpdating}
            color="primary"
            onClick={handlePaymentUpdate}
          >
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>
      {/* Success/Error Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={SlideTransition}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.type}
          variant="filled"
          sx={{
            width: "100%",
            alignItems: "center",
            fontSize: "1rem",
            py: 2,
            boxShadow: 3,
            borderRadius: 2,
          }}
        >
          <Typography variant="body1" fontWeight="medium">
            {notification.message}
          </Typography>
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ToolsPayments;
